import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Panel, PanelBody, PanelHeader, SkeletonTable } from '@/components/app/States';
import { Note } from '@/components/app/Badges';
import { Field, HistoryList, SheetSection, StatusPill, inputClass } from '@/components/app/DriftUI';
import { adminGetCompany, adminPatchCompany } from '@/lib/driftApi';
import { cn } from '@/lib/utils';

/*
  Firma og rutiner: demooppsett (navn, kortnavn, sted, beskrivelse), lesbare rutinetekster for timeføring/tillegg/avvik,
  og ærlig status for det som ikke er koblet til (SmartDok, telematikk, import, ekte tilgang, varsling) – ingen brytere uten effekt.
*/

const toForm = (c) => ({ name: c.name ?? '', shortName: c.shortName ?? '', location: c.location ?? '', description: c.description ?? '', routines: (c.routines ?? []).map((r) => ({ ...r })), reason: '' });

export function AdminFirma({ onLoaded }) {
  const [company, setCompany] = useState(null);
  const [status, setStatus] = useState('loading');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const testId = 'admin-company';

  const load = useCallback(async () => {
    setStatus((s) => (s === 'ready' ? 'refreshing' : 'loading'));
    try {
      const c = await adminGetCompany();
      setCompany(c);
      setForm(toForm(c));
      setStatus('ready');
      onLoaded?.('company', c);
    } catch {
      setStatus('error');
    }
  }, [onLoaded]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setRoutine = (i, patch) => setForm((f) => ({ ...f, routines: f.routines.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const addRoutine = () => setForm((f) => ({ ...f, routines: [...f.routines, { id: '', title: '', text: '' }] }));
  const removeRoutine = (i) => setForm((f) => ({ ...f, routines: f.routines.filter((_, idx) => idx !== i) }));

  const dirty = company && form && JSON.stringify(toForm(company)) !== JSON.stringify({ ...form, reason: '' });

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Firmanavn må fylles ut');
      return;
    }
    if (form.routines.some((r) => r.title.trim().length < 2 || r.text.trim().length < 2)) {
      toast.error('Hver rutine trenger tittel og tekst');
      return;
    }
    setSaving(true);
    try {
      const body = { reason: form.reason || null };
      for (const k of ['name', 'shortName', 'location', 'description']) if ((form[k] || '') !== (company[k] || '')) body[k] = form[k];
      if (JSON.stringify(form.routines) !== JSON.stringify(company.routines ?? [])) body.routines = form.routines.map((r) => ({ id: r.id || null, title: r.title.trim(), text: r.text.trim() }));
      const updated = await adminPatchCompany(body);
      setCompany(updated);
      setForm(toForm(updated));
      toast.success('Firmaoppsettet er lagret', { description: 'Før → etter ligger i historikken.' });
    } catch (err) {
      toast.error('Kunne ikke lagre', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (status === 'loading' && !company) return <Panel><div className="p-4"><SkeletonTable rows={5} /></div></Panel>;
  if (status === 'error' && !company) {
    return (
      <Panel>
        <div className="p-4">
          <Note tone="amber" role="alert" data-testid={`${testId}-error`}>
            Kunne ikke hente firmaoppsettet. <Button variant="link" className="h-auto px-1" onClick={load} data-testid={`${testId}-retry`}>Prøv igjen</Button>
          </Note>
        </div>
      </Panel>
    );
  }

  return (
    <div className="space-y-4">
      <Panel data-testid={`${testId}-panel`}>
        <PanelHeader
          title="Firma (demooppsett)"
          description={company.orgLabel}
          actions={
            <Button onClick={save} disabled={saving || !dirty} className="h-9 rounded-[8px] bg-[var(--hr-heading)] px-3 text-[14px] font-semibold text-white shadow-none hover:bg-[var(--hr-navy)]" data-testid={`${testId}-save-button`}>
              {saving ? 'Lagrer …' : 'Lagre'}
            </Button>
          }
        />
        <PanelBody className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Firmanavn" htmlFor="co-name" required>
              <Input id="co-name" value={form.name} onChange={(e) => set('name')(e.target.value)} maxLength={80} className={inputClass} data-testid={`${testId}-name`} />
            </Field>
            <Field label="Kortnavn (vises i portalen)" htmlFor="co-short">
              <Input id="co-short" value={form.shortName} onChange={(e) => set('shortName')(e.target.value)} maxLength={80} className={inputClass} data-testid={`${testId}-shortName`} />
            </Field>
            <Field label="Sted" htmlFor="co-loc">
              <Input id="co-loc" value={form.location} onChange={(e) => set('location')(e.target.value)} maxLength={120} className={inputClass} data-testid={`${testId}-location`} />
            </Field>
          </div>
          <Field label="Beskrivelse" htmlFor="co-desc" hint="Kort om virksomheten slik den brukes i demoen.">
            <Textarea id="co-desc" value={form.description} onChange={(e) => set('description')(e.target.value)} rows={3} maxLength={800} className="min-h-[72px] rounded-[10px] border-[var(--hr-border)] text-[16px] shadow-none lg:text-[14px]" data-testid={`${testId}-description`} />
          </Field>

          <SheetSection title="Rutiner" description="Lesbare rutinetekster for timeføring, tillegg og avvik. Ingen regler beregnes av disse.">
            <div className="space-y-3" data-testid={`${testId}-routines`}>
              {form.routines.map((r, i) => (
                <div key={r.id || `ny-${i}`} className="rounded-[10px] border border-[var(--hr-hairline)] p-3" data-testid={`${testId}-routine-${i}`}>
                  <div className="flex items-start gap-2">
                    <Input value={r.title} onChange={(e) => setRoutine(i, { title: e.target.value })} maxLength={80} placeholder="Tittel" aria-label="Rutinetittel" className={cn(inputClass, 'h-10 font-medium')} data-testid={`${testId}-routine-title-${i}`} />
                    <Button type="button" variant="ghost" size="icon" className="h-10 w-10 shrink-0 rounded-[8px]" onClick={() => removeRoutine(i)} aria-label="Fjern rutine" data-testid={`${testId}-routine-remove-${i}`}>
                      <Trash2 className="h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" />
                    </Button>
                  </div>
                  <Textarea value={r.text} onChange={(e) => setRoutine(i, { text: e.target.value })} rows={3} maxLength={1500} placeholder="Rutinetekst" aria-label="Rutinetekst" className="mt-2 min-h-[72px] rounded-[10px] border-[var(--hr-border)] text-[14px] shadow-none" data-testid={`${testId}-routine-text-${i}`} />
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" className="h-8 rounded-[8px] bg-white text-[13px]" onClick={addRoutine} data-testid={`${testId}-routine-add`}>
                <Plus className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Ny rutine
              </Button>
            </div>
          </SheetSection>

          <SheetSection title="Årsak til endringen" description="Valgfritt. Lagres i historikken sammen med før → etter.">
            <Input value={form.reason} onChange={(e) => set('reason')(e.target.value)} maxLength={300} placeholder="F.eks. «Oppdatert pauseregel i rutinen»" className={inputClass} data-testid={`${testId}-reason`} />
          </SheetSection>
        </PanelBody>
      </Panel>

      <Panel data-testid={`${testId}-integrations`}>
        <PanelHeader title="Koblinger og tilgang" description="Ærlig status. Ingen av disse er satt opp i demoen – ingenting her sender data eller endrer tilgang." />
        <ul className="divide-y divide-[var(--hr-hairline)]">
          {company.integrations.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-5 py-2.5" data-testid={`${testId}-integration-${i.id}`}>
              <div className="min-w-0">
                <div className="text-[14px] font-medium text-[var(--hr-heading)]">{i.label}</div>
                <div className="text-[13px] text-[var(--hr-muted)]">{i.note}</div>
              </div>
              <StatusPill tone="neutral">{i.statusLabel}</StatusPill>
            </li>
          ))}
        </ul>
      </Panel>

      <Panel data-testid={`${testId}-history-panel`}>
        <PanelHeader title="Historikk" description="Endringer i firmaoppsett og rutiner, før → etter. Eksempelmarkering – ikke en autentisert revisjonslogg." />
        <PanelBody>
          <HistoryList history={company.history} testId={`${testId}-history`} />
        </PanelBody>
      </Panel>
    </div>
  );
}
